import { PropertiesStore } from '@hortplus/properties-react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import URI from 'urijs';
import { trappingZoneResultsCounts } from '../../helpers/TrappingUtils';
import { useStores } from '../../stores/useStores';
import SeasonOverviewReportForm from './SeasonOverviewReportForm';
import SeasonOverviewReportPropertySummary from './SeasonOverviewReportPropertySummary';
import SeasonOverviewReportTable from './SeasonOverviewReportTable';
import SeasonalOverviewReportGraph from './SeasonalOverviewReportGraph';

const SeasonOverviewReport = (props) => {
  const { traplogStore } = useStores();

  const [loading, setLoading] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedPropertyGroup, setSelectedPropertyGroup] = useState();
  const [selectedInsectGroup, setSelectedInsectGroup] = useState();
  const [propertyGroups, setPropertyGroups] = useState([]);
  const [loadingTrappingResults, setLoadingTrappingResults] = useState(false);
  const [trappingResults, setTrappingResults] = useState({});
  const [viewMapData, setViewMapData] = useState(null);

  const showSpinner =
    loading || loadingTrappingResults || traplogStore.loadingInsectGroups;
  const showReport = selectedProperties.length > 0 && selectedInsectGroup;

  const tableData = useMemo(
    () =>
      selectedProperties
        .flatMap((property) =>
          property.trapping_zones.map((trappingZone) => {
            const trappingZoneResults = trappingResults[trappingZone.id];
            if (trappingZoneResults) {
              const counts = trappingZoneResultsCounts(
                selectedInsectGroup,
                trappingZoneResults,
                trappingZone
              );
              return {
                property,
                trappingZone,
                counts
              };
            }
          })
        )
        .filter((p) => p),
    [
      selectedProperties,
      trappingResults,
      selectedInsectGroup,
      trappingZoneResultsCounts
    ]
  );

  /**
   * Load trapping results
   */
  useEffect(() => {
    if (selectedInsectGroup && selectedProperties.length > 0) {
      setLoadingTrappingResults(true);

      const url = new URI(
        `${process.env.REACT_APP_TRAPLOG_API_URL}api/trapping-results`
      );

      url.addQuery('insect_group_id', selectedInsectGroup.id);

      selectedProperties.forEach((property) => {
        property.trapping_zones.forEach((trappingZone) => {
          url.addQuery('trapping_zones[]', trappingZone.id);
        });
      });

      fetch(url.toString(), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${props.bearerToken}`,
          SeasonalDatabase: props.selectedDatabase
        }
      })
        .then((res) => {
          if (res.ok) return res.json();
          else if (res.status === 401) props.refreshBearerToken();
          else throw Error(res.statusText);
        })
        .then((data) => {
          if (data) {
            setTrappingResults(data);
            traplogStore.trappingResults = data;
          }
        })
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') console.error(err);
        })
        .finally(() => {
          setLoadingTrappingResults(false);
        });
    } else {
      setTrappingResults({});
      traplogStore.trappingResults = {};
    }
  }, [
    props.selectedDatabase,
    props.bearerToken,
    selectedProperties,
    selectedInsectGroup
  ]);

  return (
    <Row>
      <Col>
        {!viewMapData && (
          <SeasonOverviewReportForm
            selectedDatabase={props.selectedDatabase}
            bearerToken={props.bearerToken}
            propertiesStore={props.propertiesStore}
            setLoading={setLoading}
            setSelectedProperties={setSelectedProperties}
            propertyGroups={propertyGroups}
            setPropertyGroups={setPropertyGroups}
            selectedPropertyGroup={selectedPropertyGroup}
            setSelectedPropertyGroup={setSelectedPropertyGroup}
            selectedInsectGroup={selectedInsectGroup}
            setSelectedInsectGroup={setSelectedInsectGroup}
            propertyId={props.propertyId}
          />
        )}
        {viewMapData && !showSpinner ? (
          <SeasonOverviewReportPropertySummary
            selectedDatabase={props.selectedDatabase}
            bearerToken={props.bearerToken}
            refreshBearerToken={props.refreshBearerToken}
            viewMapData={viewMapData}
            setViewMapData={setViewMapData}
            propertiesStore={props.propertiesStore}
          />
        ) : showSpinner ? (
          <Row>
            <Col className='text-center mt-4 mb-4'>
              <Spinner variant='primary' animation='border' />
            </Col>
          </Row>
        ) : showReport ? (
          <SeasonalOverviewReportGraph
            selectedProperties={selectedProperties}
            selectedInsectGroup={selectedInsectGroup}
            trappingResults={trappingResults}
          />
        ) : null}
        {!showSpinner && showReport && (
          <SeasonOverviewReportTable
            data={tableData}
            selectedPropertyGroup={selectedPropertyGroup}
            selectedInsectGroup={selectedInsectGroup}
            viewMapData={viewMapData}
            setViewMapData={setViewMapData}
            propertyId={props.propertyId}
          />
        )}
      </Col>
    </Row>
  );
};

SeasonOverviewReport.propTypes = {
  selectedDatabase: PropTypes.string,
  bearerToken: PropTypes.string.isRequired,
  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired
};

export default observer(SeasonOverviewReport);
